"use client";

/**
 * @module Dialog
 *
 * Modal dialog overlay for focused user interaction. Built on Radix UI Dialog primitive.
 * Includes backdrop overlay, close button, and focus trapping.
 *
 * @see {@link https://ui.shadcn.com/docs/components/dialog Shadcn Dialog}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/dialog Radix Dialog}
 *
 * @example
 * // Basic dialog
 * <Dialog>
 *   <DialogTrigger asChild>
 *     <Button>Open Dialog</Button>
 *   </DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Are you sure?</DialogTitle>
 *       <DialogDescription>
 *         This action cannot be undone.
 *       </DialogDescription>
 *     </DialogHeader>
 *     <DialogFooter>
 *       <Button>Confirm</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 *
 * @example
 * // Controlled dialog
 * const [open, setOpen] = useState(false);
 *
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Edit Profile</DialogTitle>
 *     </DialogHeader>
 *     <form onSubmit={() => setOpen(false)}>
 *       ...
 *     </form>
 *   </DialogContent>
 * </Dialog>
 *
 * @example
 * // Fullscreen on mobile, modal on desktop
 * <Dialog>
 *   <DialogTrigger asChild>
 *     <Button>Sign up</Button>
 *   </DialogTrigger>
 *   <DialogContent fullScreenOnMobile>
 *     <DialogHeader>
 *       <DialogTitle>Create your account</DialogTitle>
 *     </DialogHeader>
 *     ...
 *   </DialogContent>
 * </Dialog>
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import type * as React from "react";
import type { Ref } from "react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { headingVariants } from "./typography/heading";
import { textVariants } from "./typography/text";

/** Root component that manages dialog open/closed state. */
const Dialog = DialogPrimitive.Root;

/** Element that opens the dialog when clicked. Use `asChild` to wrap custom buttons. */
const DialogTrigger = DialogPrimitive.Trigger;

/** Portal for rendering dialog outside the DOM hierarchy. */
const DialogPortal = DialogPrimitive.Portal;

/** Closes the dialog when clicked. Use `asChild` to wrap custom close buttons. */
const DialogClose = DialogPrimitive.Close;

/** Semi-transparent backdrop behind the dialog. Animated fade on open/close. */
const DialogOverlay = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: Ref<React.ElementRef<typeof DialogPrimitive.Overlay>>;
}) => <DialogPrimitive.Overlay className={cn("data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60 data-[state=closed]:animate-out data-[state=open]:animate-in", className)} ref={ref} {...props} data-sentry-element="DialogPrimitive.Overlay" data-sentry-component="DialogOverlay" data-sentry-source-file="dialog.tsx" />;

/**
 * Visual variants for the dialog content surface.
 *
 * The default (`fullScreenOnMobile={false}`) renders a centered modal at every viewport.
 * `fullScreenOnMobile={true}` renders fullscreen below the `md` breakpoint and reverts
 * to the centered modal at `md` and up — useful for forms and multi-step flows
 * that need the full mobile viewport but should remain a modal on desktop.
 */
const dialogContentVariants = cva(cn("z-50 grid gap-4 bg-white shadow-lg duration-200", "border-gray-150 border-solid dark:border-gray-800 dark:bg-gray-875", "data-[state=closed]:animate-out data-[state=open]:animate-in", "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"), {
  variants: {
    fullScreenOnMobile: {
      true: cn("fixed inset-0 h-full w-full max-md:border-none p-8", "md:inset-auto md:top-[50%] md:left-[50%]", "md:h-auto md:max-w-lg", "md:translate-x-[-50%] md:translate-y-[-50%]", "md:rounded-lg md:border", "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95"),
      false: cn("fixed top-[50%] left-[50%] w-full max-w-lg border p-6", "translate-x-[-50%] translate-y-[-50%]", "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95", "sm:rounded-lg")
    }
  },
  defaultVariants: {
    fullScreenOnMobile: false
  }
});
type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & VariantProps<typeof dialogContentVariants> & {
  ref?: Ref<React.ElementRef<typeof DialogPrimitive.Content>>;
};

/**
 * Main dialog container. Centered on screen with close button.
 * Includes overlay, focus trap, and Escape key handling.
 *
 * @param fullScreenOnMobile - When `true`, renders fullscreen below the `md` breakpoint
 *   and centered modal at `md` and up. Default `false` (centered modal at every viewport).
 */
const DialogContent = ({
  className,
  children,
  fullScreenOnMobile,
  ref,
  ...props
}: DialogContentProps) => <DialogPortal data-sentry-element="DialogPortal" data-sentry-component="DialogContent" data-sentry-source-file="dialog.tsx">
    <DialogOverlay data-sentry-element="DialogOverlay" data-sentry-source-file="dialog.tsx" />
    <DialogPrimitive.Content className={cn(dialogContentVariants({
    fullScreenOnMobile
  }), className)} ref={ref} {...props} data-sentry-element="DialogPrimitive.Content" data-sentry-source-file="dialog.tsx">
      {children}
      <DialogPrimitive.Close asChild className="absolute top-4 right-4 opacity-100 ring-offset-white transition-opacity hover:opacity-100 outline-hidden focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-gray-500 dark:ring-offset-gray-950 dark:data-[state=open]:bg-gray-800 dark:data-[state=open]:text-gray-400 dark:focus-visible:ring-gray-300" data-sentry-element="DialogPrimitive.Close" data-sentry-source-file="dialog.tsx">
        <Button aria-label="Close" size="icon" variant="transparent" data-sentry-element="Button" data-sentry-source-file="dialog.tsx">
          <X className="h-5 w-5" data-sentry-element="X" data-sentry-source-file="dialog.tsx" />
          <span className="sr-only">Close</span>
        </Button>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>;

/** Header section for dialog title and description. Centered on mobile, left-aligned on desktop. */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />;
DialogHeader.displayName = "DialogHeader";

/** Footer section for action buttons. Stacks on mobile, horizontal on desktop. */
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
DialogFooter.displayName = "DialogFooter";

/** Dialog title with Echo typography. Required for accessibility. */
const DialogTitle = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: Ref<React.ElementRef<typeof DialogPrimitive.Title>>;
}) => <DialogPrimitive.Title className={cn(headingVariants({
  variant: "h5",
  fontFamily: "brand"
}), className)} ref={ref} {...props} data-sentry-element="DialogPrimitive.Title" data-sentry-component="DialogTitle" data-sentry-source-file="dialog.tsx" />;

/** Accessible description text below the title. */
const DialogDescription = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: Ref<React.ElementRef<typeof DialogPrimitive.Description>>;
}) => <DialogPrimitive.Description className={cn(textVariants(), "text-sm", className)} ref={ref} {...props} data-sentry-element="DialogPrimitive.Description" data-sentry-component="DialogDescription" data-sentry-source-file="dialog.tsx" />;
export { Dialog, DialogClose, DialogContent, type DialogContentProps, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, dialogContentVariants };